<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/userStore";
import { storeToRefs } from "pinia";
import SidebarMenu from "@/components/SidebarMenu.vue";
import CreditFooter from "@/components/CreditFooter.vue";
import TopBar from "@/components/TopBar.vue";
import ReleaseNoteBanner from "@/components/ReleaseNoteBanner.vue";
import InfoBanner, { type CTA } from "@comp-lib/components/InfoBanner.vue";

interface BannerCTAs { primaryCTA?: CTA, secondaryCTA?: CTA }

const userStore = useUserStore();
const { appState, isSetupIncomplete, currentTenant } = storeToRefs(userStore);

const route = useRoute();
const router = useRouter();

const isMenuOpen = ref(false);
const overlayEl = ref<HTMLElement | null>(null);

function toggleOpen() {
  isMenuOpen.value = !isMenuOpen.value;
}

function checkClickTarget(e: MouseEvent) {
  if (!isMenuOpen.value) return;
  if (overlayEl.value && e.composedPath().includes(overlayEl.value)) {
    isMenuOpen.value = false;
  }
}

const bannerCTAs = computed<BannerCTAs>(() => {
  const CTAs: BannerCTAs = {};

  if (!appState.value.isConnectwiseConfigured) {
    CTAs.primaryCTA = {
      label: "Set up Connectwise",
      handler: () => router.push({ name: "iframe-user-connectwise-configuration", params: { tenant_id: String(userStore.currentTenant?.id) } })
    };
  }

  if (!appState.value.accountingConfigurations?.length) {
    CTAs.secondaryCTA = {
      label: "Set up QuickBooks Online",
      handler: () => router.push({ name: "iframe-user-accounting-config-new", params: { tenant_id: String(userStore.currentTenant?.id) } })
    };
  }

  return CTAs;
});

watch(() => route.name, () => {
  if (isMenuOpen.value) toggleOpen();
});

onMounted(() => {
  document.body.addEventListener("click", checkClickTarget);
});
onBeforeUnmount(() => {
  document.body.removeEventListener("click", checkClickTarget);
});
</script>

<template>
  <TopBar :isSidebarOpen="isMenuOpen" @@openMenu="isMenuOpen = $event" />
  <div class="layout-wrapper">
    <SidebarMenu :isSidebarOpen="isMenuOpen" />

    <main class="scrollbar">
      <div ref="overlayEl" :class="['overlay', { open: isMenuOpen }]" />
      <div class="content">
        <ReleaseNoteBanner />
        <InfoBanner
          v-if="!!currentTenant && isSetupIncomplete"
          title="Complete your Mobius setup"
          v-bind="bannerCTAs"
        >
          <template #text>
            <p>Lots of exciting features are waiting for you! Finish setting up Mobius now to unlock its full potential.</p>
          </template>
        </InfoBanner>
        <slot />
      </div>
      <CreditFooter />
    </main>
  </div>
</template>

<style lang="scss" scoped>
.layout-wrapper {
  flex-grow: 1;
  display: flex;
  overflow: hidden;
  position: relative;
  @media (max-width: map.get(v.$breakpoints, "md")) {
    flex-direction: column;
  }
  main {
    overflow: auto;
    flex-grow: 1;
    padding: 30px 30px 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    @media (max-width: map.get(v.$breakpoints, "md")) {
      padding: 30px 10px 0;
    }
    .content {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      .banner {
        margin-bottom: 20px;
      }
    }
  }
  .overlay {
    display: none;
    @media (max-width: map.get(v.$breakpoints, "md")) {
      display: unset;
      position: fixed;
      left: 0;
      top: 0;
      height: 100vh;
      width: 100%;
      background-color: map.get(v.$gray, "900");
      opacity: 0;
      visibility: hidden;
      transition: opacity .2s, visibility .2s;
      z-index: 3;
    }
    &.open {
      opacity: .5;
      visibility: visible;
    }
  }
}
</style>
